import { type Icon } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'

import type { Role } from './useCurrentEmployeeRole'

const useDescription = (
  currentEmployee: Employee | null | undefined,
  form: LeaveForm,
  isMedical: boolean
): string => {
  if (isMedical) {
    return i18n.t('time_off.medical_leave.confirm_header.description')
  }

  const manager = currentEmployee ? currentEmployee.timeoffManager : null
  const timeoffManager = manager && employeeUser(manager)?.fullName()

  const adminDescription = i18n.t('time_off.employee.add_new.subtitle.admin', {
    timeoffManager,
  })

  // !employee means user can select employee. So is an admin
  if (!currentEmployee) return adminDescription

  const canApprove = currentEmployee.policy.can('approveLeaves')

  if (canApprove) return adminDescription

  if (!currentEmployee.timeoffManager) {
    return i18n.t('time_off.employee.add_new.subtitle.no_supervisor', {
      timeoffManager,
    })
  }

  if (form.isSickType()) {
    return i18n.t('time_off.employee.add_new.subtitle.sick', {
      timeoffManager,
    })
  }

  return i18n.t('time_off.employee.add_new.subtitle.basic', {
    timeoffManager,
  })
}

const useTitle = (currentEmployeeRole: Role, isMedical: boolean): string => {
  if (isMedical) {
    return i18n.t('time_off.medical_leave.confirm_header.title')
  }

  if (currentEmployeeRole === 'admin') {
    return i18n.t('time_off.employee.add_new.title.admin')
  }

  return i18n.t('time_off.employee.add_new.title.basic')
}

interface Props {
  isMedical: boolean
  currentEmployee: Employee | null | undefined
  form: LeaveForm
  currentEmployeeRole: Role
}
interface ReturnType {
  illustration: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description: string
}

const useModalProps = ({
  isMedical,
  currentEmployee,
  currentEmployeeRole,
  form,
}: Props): ReturnType => {
  const illustration = isMedical ? 'user-tie' : 'beach'
  const title = useTitle(currentEmployeeRole, isMedical)
  const description = useDescription(currentEmployee, form, isMedical)

  return {
    illustration,
    title,
    description,
  }
}

export default useModalProps
